/**
 * 审查发现的表态词汇表（唯一定义处）。
 *
 * 三值语义与否决分类词表见 docs/glossary.md「表态」「否决分类」，设计见
 * docs/design/2026-08-20-AI结论表态与反馈设计.md。底层枚举值跨场景统一
 * （accepted / rejected），界面文案按场景分开——审查发现是一个判断，否定它
 * 就是判定误报，因此这里用「判为误报」而不是「已拒绝」。
 *
 * 后端对应定义在 `enums.FindingProcessedStatus` / `AiVerdictReasonCode` 与
 * service 的 `_FINDING_VERDICT_LABELS` / `_VERDICT_REASON_LABELS`（导出件用），
 * 措辞调整必须两侧同步。
 */

import type { AiFindingProcessed, AiVerdictReasonCode } from "../types/aiReview";

/** 表态在审查场景的文案。 */
export const AI_FINDING_VERDICT_LABEL: Record<AiFindingProcessed, string> = {
  unprocessed: "未表态",
  accepted: "已采纳",
  rejected: "判为误报",
};

/** 表态动作的按钮文案——动词，与上面的状态名词分开，避免按钮读成当前状态。 */
export const AI_FINDING_VERDICT_ACTION_LABEL: Record<
  Exclude<AiFindingProcessed, "unprocessed">,
  string
> = {
  accepted: "采纳",
  rejected: "不认同",
};

/** antd Tag 语义色：采纳为正向，误报为中性——误报是 AI 的问题，不是律师的风险。 */
export const AI_FINDING_VERDICT_TAG_COLOR: Record<AiFindingProcessed, string> = {
  unprocessed: "default",
  accepted: "green",
  rejected: "default",
};

/**
 * 表态在对话场景的文案（阶段二）。
 *
 * 与审查场景刻意不同：审查发现是一个**判断**，否定它就是判定误报；对话回答是
 * 一段说明，律师对它的态度是认同与否，说成「误报」不通。底层枚举值两处统一
 * （accepted / rejected），跨场景统计不受文案差异影响。
 */
export const AI_MESSAGE_VERDICT_LABEL: Record<AiFindingProcessed, string> = {
  unprocessed: "未表态",
  accepted: "已认同",
  rejected: "不认同",
};

/** 对话场景的表态动作文案——动词，与上面的状态名词分开。 */
export const AI_MESSAGE_VERDICT_ACTION_LABEL: Record<
  Exclude<AiFindingProcessed, "unprocessed">,
  string
> = {
  accepted: "认同",
  rejected: "不认同",
};

/**
 * 否决分类词表，按律师实际使用频度排序（「不适用本案情形」在前段——它是最常见
 * 的否决理由，且必须与「AI 说错了」类分类分开：前者不是模型缺陷，混在一起会让
 * 统计指向错误的改进方向）。
 *
 * 全部选填：否决本身一次点击即完成，任何必填项都会让律师在赶交付时干脆不表态。
 */
export const AI_VERDICT_REASON_OPTIONS: ReadonlyArray<{
  value: AiVerdictReasonCode;
  label: string;
}> = [
  { value: "contradicts_source", label: "与原文不符" },
  { value: "not_applicable", label: "不适用本案情形" },
  { value: "wrong_legal_basis", label: "法律依据错误或过期" },
  { value: "missed_key_point", label: "遗漏关键要点" },
  { value: "too_generic", label: "结论过于笼统无法使用" },
  { value: "improper_wording", label: "表述或术语不当" },
  { value: "other", label: "其他" },
];

const REASON_LABEL: Record<string, string> = Object.fromEntries(
  AI_VERDICT_REASON_OPTIONS.map((o) => [o.value, o.label]),
);

/** 否决分类文案。未选分类时返回空串——不写「未分类」之类的占位，那会读成
 * 律师做过一次选择。 */
export function formatVerdictReason(
  code: AiVerdictReasonCode | null | undefined,
): string {
  return code ? (REASON_LABEL[code] ?? code) : "";
}

/** 继承提示文案：本行由上一轮的哪种表态匹配而来。非继承行返回 null。 */
export function formatInheritedVerdictHint(
  inheritedProcessed: AiFindingProcessed | null | undefined,
): string | null {
  if (inheritedProcessed === "rejected") return "上一轮判为误报";
  if (inheritedProcessed === "accepted") return "上一轮已采纳";
  return null;
}
